import { useCallback, useMemo } from "react";

import { useModulePermissions } from "utils/modulePermissions";

export const PROJECT_TAB_PERMISSIONS = {
  won: "projects.tabs.won.view",
  live: "projects.tabs.live.view",
  pause: "projects.tabs.pause.view",
  auto_pause: "projects.tabs.auto_pause.view",
  invoiced: "projects.tabs.invoiced.view",
  invoice_at_zero: "projects.tabs.invoice_at_zero.view",
  closed: "projects.tabs.closed.view",
};

export const PROJECT_ACTION_PERMISSIONS = {
  search: "projects.actions.search",
  export: "projects.actions.export",
  expand_details: "projects.actions.expand_details",
  update_project_status: "projects.actions.update_project_status",
  open_project_details: "projects.actions.open_project_details",
  submit_project_details: "projects.actions.submit_project_details",
  open_add_country_links: "projects.actions.open_add_country_links",
  submit_add_country_links: "projects.actions.submit_add_country_links",
  open_country_details: "projects.actions.open_country_details",
  submit_country_details: "projects.actions.submit_country_details",
  update_country_status: "projects.actions.update_country_status",
  open_add_vendor: "projects.actions.open_add_vendor",
  submit_add_vendor: "projects.actions.submit_add_vendor",
  open_vendor_details: "projects.actions.open_vendor_details",
  submit_vendor_details: "projects.actions.submit_vendor_details",
  update_vendor_status: "projects.actions.update_vendor_status",
  copy_vendor_links: "projects.actions.copy_vendor_links",
  view_survey_responses: "projects.actions.view_survey_responses",
  upload_final_ids: "projects.actions.upload_final_ids",
  export_survey_responses: "projects.actions.export_survey_responses",
  view_user_url: "projects.actions.view_user_url",
  copy_user_url: "projects.actions.copy_user_url",
  view_vendor_url: "projects.actions.view_vendor_url",
  copy_vendor_url: "projects.actions.copy_vendor_url",
  mail: "projects.actions.mail",
  chat: "projects.actions.chat",
  manage_project_link_hit_gap: "projects.actions.manage_project_link_hit_gap",
  manage_country_link_hit_gap: "projects.actions.manage_country_link_hit_gap",
  manage_vendor_link_hit_gap: "projects.actions.manage_vendor_link_hit_gap",
};

export const PROJECT_FIELD_PERMISSIONS = {
  project_code: ["projects.fields.project_code.view"],
  project_title: ["projects.fields.project_title.view"],
  project_name: ["projects.fields.project_title.view"],
  country_project_name: ["projects.fields.country_project_name.view"],
  client: ["projects.fields.client.view"],
  client_id: ["projects.fields.client.view"],
  client_name: ["projects.fields.client.view"],
  client_code: ["projects.fields.client.view"],
  project_manager: ["projects.fields.project_manager.view"],
  project_manager_team: ["projects.fields.project_manager_team.view"],
  project_type: ["projects.fields.project_type.view"],
  study_type: ["projects.fields.study_type.view"],
  required_n: ["projects.fields.required_n.view"],
  overall_quota: ["projects.fields.overall_quota.view"],
  overall_clicks_quota: ["projects.fields.overall_quota.view"],
  country_quota: ["projects.fields.country_quota.view"],
  country_clicks_quota: ["projects.fields.country_quota.view"],
  vendor_quota: ["projects.fields.vendor_quota.view"],
  vendor_clicks_quota: ["projects.fields.vendor_quota.view"],
  ir: ["projects.fields.ir.view"],
  loi: ["projects.fields.loi.view"],
  cpi: ["projects.fields.cpi.view"],
  bid_cpi: ["projects.fields.cpi.view"],
  won_cpi: ["projects.fields.cpi.view"],
  target_cpi: ["projects.fields.cpi.view"],
  status: ["projects.fields.status.view"],
  actions: ["projects.fields.actions.view"],
  comments: ["projects.fields.comments.view"],
  attachment_docs: ["projects.fields.attachment_docs.view"],
  project_launch_date: ["projects.fields.project_launch_date.view"],
  country: ["projects.fields.country.view"],
  country_id: ["projects.fields.country.view"],
  country_name: ["projects.fields.country.view"],
  targeting: ["projects.fields.targeting.view"],
  live_url: ["projects.fields.live_url.view"],
  test_url: ["projects.fields.test_url.view"],
  is_secure: ["projects.fields.is_secure.view"],
  is_captcha_security: ["projects.fields.is_captcha_security.view"],
  device_type: ["projects.fields.device_type.view"],
  vendors: ["projects.fields.vendors.view"],
  vendor_id: ["projects.fields.vendors.view"],
  feasibility: ["projects.fields.feasibility.view"],
  required_field_time_days: ["projects.fields.required_field_time_days.view"],
  stats: ["projects.fields.stats.view"],
  complete_device_browser_counting: ["projects.fields.complete_device_browser_counting.view"],
  internal_user_metrics: ["projects.fields.internal_user_metrics.view"],
  rtr: ["projects.fields.rtr.view"],
  revenue: ["projects.fields.revenue.view"],
  link_type: ["projects.fields.link_type.view"],
  pid: ["projects.fields.pid.view"],
  rid: ["projects.fields.rid.view"],
  start_ip: ["projects.fields.start_ip.view"],
  callback_ip: ["projects.fields.callback_ip.view"],
  device: ["projects.fields.device.view"],
  browser: ["projects.fields.browser.view"],
  os: ["projects.fields.os.view"],
  user_agent: ["projects.fields.user_agent.view"],
  source_type: ["projects.fields.source_type.view"],
  client_status: ["projects.fields.client_status.view"],
  user_url: ["projects.fields.user_url.view"],
  vendor_url: ["projects.fields.vendor_url.view"],
  created_at: ["projects.fields.created_at.view"],
  updated_at: ["projects.fields.updated_at.view"],
};

export const useProjectPermissions = (permissions = [], userPermission = []) => {
  const { grantedPermissions, hasModulePermission } = useModulePermissions(
    permissions,
    userPermission,
    "projects"
  );

  const canViewPage = useMemo(
    () => hasModulePermission("projects.pages.list.view"),
    [hasModulePermission]
  );

  const canTab = useCallback(
    (tab) => hasModulePermission(PROJECT_TAB_PERMISSIONS[tab]),
    [hasModulePermission]
  );

  const canAction = useCallback(
    (action) => hasModulePermission(PROJECT_ACTION_PERMISSIONS[action]),
    [hasModulePermission]
  );

  const canSetProjectStatus = useCallback(
    (status) => hasModulePermission(`projects.actions.project_status.${status}`),
    [hasModulePermission]
  );

  const canSetCountryStatus = useCallback(
    (status) => hasModulePermission(`projects.actions.country_status.${status}`),
    [hasModulePermission]
  );

  const canField = useCallback(
    (field) => hasModulePermission(PROJECT_FIELD_PERMISSIONS[field] || [`projects.fields.${field}.view`]),
    [hasModulePermission]
  );

  const filterPayloadByFields = useCallback(
    (payload, fieldMap = {}) =>
      Object.entries(payload || {}).reduce((nextPayload, [payloadKey, value]) => {
        const fieldKey = fieldMap[payloadKey] || payloadKey;

        if (canField(fieldKey)) {
          nextPayload[payloadKey] = value;
        }

        return nextPayload;
      }, {}),
    [canField]
  );

  return {
    grantedPermissions,
    canViewPage,
    canTab,
    canAction,
    canSetProjectStatus,
    canSetCountryStatus,
    canField,
    filterPayloadByFields,
  };
};
